import { Dialog } from "primereact/dialog";
import { JTranslation } from "../../helpers/jTranslate";
import { Input } from "antd";
import { Checkbox } from "primereact/checkbox";
import { Badge } from "primereact/badge";
import { capitalize, cloneDeep } from "lodash";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  getStaffAvailabilityForTheDay,
  Staff,
  updateMultipleAssignShiftUsers
} from "../../helpers/workTimeScheduleHelper";
import React from "react";
import useMutationHook from "../../hooks/useMutationHook";
import { useQueryClient } from "react-query";
import { ToastMessageProps } from "../../constants/staticTypes";
import { STAFFS_WITH_SCHEDULES_AND_SHIFT } from "../../constants/queryKeys";
import { NO_ROLES_ASSIGNED } from "../../constants/strings";
import { convertToLocalByOffset } from "../../helpers/utils";

type Props = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<any>>;
  staffList: Staff[];
  selectedShift: any;
  scheduleId: string;
  setToastMessage: Dispatch<SetStateAction<ToastMessageProps>>
  callBackFn?: () => void
};

type CustomStaff = Staff & { haveShift: boolean, availabilityText: JSX.Element | null | undefined, alreadyAssigned: boolean };

type multipleShiftFormData = {
  sourceShiftId: string;
  tenantStaffIds: string[];
}

function AddMultipleShiftAssignmentModal({
  visible,
  setVisible,
  staffList,
  selectedShift,
  scheduleId,
  setToastMessage,
  callBackFn
}: Readonly<Props>) {
  const queryClient = useQueryClient();
  const addMultipleShiftUserMutation = useMutationHook(queryClient, false);
  const [staffListWithShift, setStaffListWithShift] = useState<CustomStaff[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeModal = () => {
    if (!visible) return;
    setVisible(undefined);
    setSearchText('');
    setStaffListWithShift([]);
  };

  const headerElement = (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="m-0">
          <JTranslation typeCase="capitalize" text="Assign to staffs" />
        </h5>
        <i className="pi pi-times" role="button" onClick={closeModal} />
      </div>
      <small style={{ fontSize: "0.585em", color: "#939393", fontWeight: "normal" }}      >
        {`${capitalize(selectedShift?.assignedShifts?.role)} (${selectedShift?.assignedShifts?.startTime} - ${selectedShift?.assignedShifts?.endTime})`}
      </small>
    </div>
  );

  const onceApplied = (async (message: string, variant: string) => {
    setToastMessage({ message, variant, show: true });
    await queryClient.refetchQueries([STAFFS_WITH_SCHEDULES_AND_SHIFT]);
    setIsLoading(false);
    closeModal();

    if(callBackFn) callBackFn();
  });

  const handleApply = () => {
    const selectedStaffIds = staffListWithShift
      ?.filter(staff => staff?.haveShift && !staff?.alreadyAssigned)
      ?.map(staff => staff?.id);

    if (selectedStaffIds.length) {
      setIsLoading(true);
      const param: multipleShiftFormData = {
        sourceShiftId: selectedShift.id,
        tenantStaffIds: selectedStaffIds
      }

      updateMultipleAssignShiftUsers(
        addMultipleShiftUserMutation,
        scheduleId,
        param,
        onceApplied,
        onceApplied
      );
    } else {
      setToastMessage({ message: 'Please select at least one staff', variant: 'warning', show: true });
    }
  }

  const footerContent = (
    <div className="">
      <button
        type="button"
        className="btn btn-custom-primary w-100"
        onClick={handleApply}
        disabled={isLoading}
      >
        <span className="btn-text">
          {!isLoading ? (
            <JTranslation typeCase="pascal" text="Apply" />
          ) : (
            <span className="d-flex align-items-center justify-content-center">
              <JTranslation typeCase="pascal" text="Applying" />
              <i className="pi pi-spin pi-spinner ms-2" style={{ fontSize: '1rem' }} />
            </span>
          )}
        </span>
      </button>
    </div>
  );

  const checkIfStaffHaveShift = useCallback((staff: CustomStaff) => {
    const parsedSelectedDay = convertToLocalByOffset(selectedShift.scheduleDate, { type: 'string', format: 'YYYY-MM-DD' });
    // const parsedSelectedDay = selectedDay?.format("YYYY-MM-DD");
    const staffShifts = staff.shifts[parsedSelectedDay] ?? [];
    const checkIfStaffHaveShift = staffShifts.some((shift: any) => {
      const currentStart = dayjs(selectedShift.assignedShifts.startTime, "hh:mm A");
      const currentEnd = dayjs(selectedShift.assignedShifts.endTime, "hh:mm A");
      const otherStart = dayjs(shift.assignedShifts.startTime, "hh:mm A");
      const otherEnd = dayjs(shift.assignedShifts.endTime, "hh:mm A");
      return (
        (currentStart.isSame(otherStart) || currentStart.isBetween(otherStart, otherEnd)) ||
        (currentEnd.isSame(otherEnd) || currentEnd.isBetween(otherStart, otherEnd)) ||
        (otherStart.isBetween(currentStart, currentEnd))
      );
    });
    return checkIfStaffHaveShift;
  }, [selectedShift]);

  const handleRemoveShiftStaff = (staff: CustomStaff) => {
    const updatedStaffList = cloneDeep(staffListWithShift).map((currentStaff: CustomStaff) => {
      if (currentStaff.id === staff.id) {
        currentStaff.haveShift = !currentStaff.haveShift;
      }
      return currentStaff;
    });
    setStaffListWithShift(updatedStaffList);
  }

  useEffect(() => {
    if (visible && staffList.length) {
      const selectedDay = dayjs(new Date(selectedShift.scheduleDate));
      const dayString = selectedDay.format("dddd").toLowerCase();
      const formattedWeek = selectedDay;

      const updatedStaffList = (staffList as CustomStaff[]).map(staff => {
        // mark if staff is already assigned to the shift
        const isStaffAssigned = checkIfStaffHaveShift(staff);
        staff.alreadyAssigned = isStaffAssigned;
        staff.haveShift = isStaffAssigned;

        // Update staff list with availability
        const availabilityText = getStaffAvailabilityForTheDay({
          day: dayString,
          availabilities: staff.availabilities,
          selectedWeek: formattedWeek,
          type: "schedule",
        });

        return {
          ...staff,
          alreadyAssigned: isStaffAssigned,
          haveShift: isStaffAssigned,
          availabilityText,
        };
      })?.filter(staff => staff !== null);

      setStaffListWithShift(updatedStaffList);
    }
  }, [visible, staffList, selectedShift]);

  return (
    <Dialog
      visible={visible}
      modal
      header={headerElement}
      footer={footerContent}
      style={{ width: "30rem" }}
      onHide={closeModal}
      draggable={false}
      closeIcon={false}
      closable={false}
      closeOnEscape={false}
      pt={{
        content: { style: { padding: "0rem 1rem" }},
        footer: { style: { padding: "1rem" }},
        header: { style: { padding: "1.25rem 1rem" }},
      }}
    >
      <Input
        className="mb-3 border-secondary rounded"
        placeholder="Search staff"
        prefix={<i className="pi pi-search text-secondary" />}
        onChange={(event) => setSearchText(event.target.value ?? '')}
      />

      <div className="overflow-auto px-1" style={{ maxHeight: "300px" }}>
        {staffListWithShift.map((staff: CustomStaff, index: number) => {
          const roles = staff?.jobRoles
            .map((role: any) => capitalize(role?.jobRole?.jobRoleName))
            .filter(Boolean)
            .join(", ");

          // Filter staff list by search text
          const staffName = staff?.preferredName ?? staff?.firstName ?? staff?.lastName;
          if (searchText && !staffName?.toLowerCase().includes(searchText.toLowerCase())) return <></>;

          return (
            <div key={`${selectedShift?.id}-${staff?.id}-${index + 1}}`} className="d-flex flex-row align-items-center mb-2">
              <Checkbox
                className="flex-grow-0 me-3"
                checked={staff?.haveShift}
                disabled={staff?.alreadyAssigned}
                onClick={(event) => {
                  handleRemoveShiftStaff(staff);
                  event.stopPropagation();
                }}
              />
              <div className="flex-grow-1 d-flex flex-row align-items-center">
                {/* <UserAvatar imageUrl={staff?.signedUrl ?? staff?.photoThumbnail} name={"profile-img"} /> */}
                <div className="d-flex flex-column py-2" style={{ lineHeight: "1rem" }}>
                  <p className="m-0">
                    {capitalize(staff?.preferredName) ??
                      capitalize(`${staff?.firstName} ${staff?.lastName}`)}
                  </p>
                  <small
                    className="overflow-ellipsis m-0"
                    style={{
                      fontSize: "0.785em",
                      color: "#939393",
                      width: "12rem",
                    }}
                    title={roles}
                  >
                    {roles?.trim().length ? roles : NO_ROLES_ASSIGNED}
                  </small>
                </div>
              </div>
              <Badge
                className="mx-2 flex-grow-0"
                value={<JTranslation typeCase="pascal" text={staff?.availabilityText ? 'N/A' : 'Available'} />}
                style={{ maxWidth: "70px" }}
                severity={staff?.availabilityText ? 'danger' : 'success'}
              />
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

export default React.memo(AddMultipleShiftAssignmentModal);
